import { randomUUID } from '@/utils/uuid-util'
import { action, observable } from 'mobx'
import { required, validationContext } from 'validx'
import { Id } from '@/infra/id'
import { asNumber } from '@/utils/number-util'

/**
 * The source of a job scope item.
 */
export type JobScopeItemSource = 'Unknown' | 'Job' | 'Added'

/**
 * A custom service item in the job scope being edited.
 */
export class JobScopeCustomItemViewModel {
  /**
   * The custom item's ID; generated for added items, set from the job for existing ones.
   */
  @observable accessor id = Id.none

  /**
   * The service name.
   */
  @observable accessor name = ''

  /**
   * The quantity after the update.
   */
  @observable accessor newQuantity = 1

  /**
   * The quantity currently on the job.
   */
  @observable accessor currentQuantity = 0

  /**
   * The client price.
   */
  @observable accessor price = 0

  /**
   * The Pro price.
   */
  @observable accessor proPrice = 0

  /**
   * Where the item comes from.
   */
  @observable accessor source: JobScopeItemSource = 'Unknown'

  /**
   * Validation context for the item.
   */
  readonly validation = validationContext(this, {
    name: [required('Service name is required.')],
  })

  constructor(source: JobScopeItemSource) {
    this.source = source
    this.id = this.source === 'Added' ? randomUUID() : Id.none
    this.newQuantity = this.source === 'Job' ? this.currentQuantity : 1
  }

  /**
   * Validates the item and returns whether it is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Sets the ID.
   */
  @action.bound
  setId(value: Id) {
    this.id = value
  }

  /**
   * Sets the quantity currently on the job; existing items start at that quantity.
   */
  @action.bound
  setCurrentQuantity(value: number) {
    this.currentQuantity = value

    if (this.source === 'Job') {
      this.newQuantity = value
    }
  }

  /**
   * Sets the quantity after the update; clearing the field means zero.
   */
  @action.bound
  setQuantity(value: number | null) {
    this.newQuantity = value ?? 0
  }

  /**
   * Sets the service name.
   */
  @action.bound
  setName(value: string) {
    this.name = value
  }

  /**
   * Sets the client price.
   */
  @action.bound
  setPrice(value: number | string) {
    this.price = asNumber(value)
  }

  /**
   * Sets the Pro price.
   */
  @action.bound
  setProPrice(value: number | string) {
    this.proPrice = asNumber(value)
  }
}
